import * as React from "react";
import { useCallback } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import { menuItems } from "../utils/data";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { AdminPanelSettings, ExitToApp, NotificationImportant } from "@mui/icons-material";
import { Avatar, Tooltip } from "@mui/material";

const INACTIVITY_MS = 15 * 60 * 1000;
const WARNING_SECS = 20;
const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1, 0, 2),
  ...theme.mixins.toolbar,
  backgroundColor: "#000C66",
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "#000C66",
  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const PermanentDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      backgroundColor: "#000C66",
      borderRight: "none",
      boxShadow: "4px 0 20px rgba(0,12,102,0.3)",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      backgroundColor: "#000C66",
      borderRight: "none",
      boxShadow: "4px 0 20px rgba(0,12,102,0.3)",
    },
  }),
}));

const mobilePaperSx = {
  backgroundColor: "#000C66",
  borderRight: "none",
  boxShadow: "4px 0 20px rgba(0,12,102,0.3)",
  width: drawerWidth,
};

const isActive = (path, location) => location.pathname === path;

export default function MiniDrawer(props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [show, setShow] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(false);
  const [userEmail, setUserEmail] = React.useState("");
  const [userName, setUserName] = React.useState("");
  const [userRole, setUserRole] = React.useState("");

  const [inactiveWarning, setInactiveWarning] = React.useState(false);
  const [countdown, setCountdown] = React.useState(WARNING_SECS);
  const lastActivityRef = React.useRef(Date.now());
  const warningActiveRef = React.useRef(false);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) handleDrawerClose();
  };

  const logout = useCallback(async () => {
    await signOut(auth);
    navigate("/");
  }, [navigate]);

  // Inactivity detection
  React.useEffect(() => {
    const updateActivity = () => {
      if (!warningActiveRef.current) lastActivityRef.current = Date.now();
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, updateActivity));

    const checkInterval = setInterval(() => {
      if (!warningActiveRef.current && Date.now() - lastActivityRef.current >= INACTIVITY_MS) {
        warningActiveRef.current = true;
        setInactiveWarning(true);
        setCountdown(WARNING_SECS);
      }
    }, 15000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity));
      clearInterval(checkInterval);
    };
  }, []);

  // Countdown timer when warning is shown
  React.useEffect(() => {
    if (!inactiveWarning) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); logout(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [inactiveWarning, logout]);

  const continueSession = () => {
    warningActiveRef.current = false;
    lastActivityRef.current = Date.now();
    setInactiveWarning(false);
    setCountdown(WARNING_SECS);
  };

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      setUserEmail(user.email);
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          setUserRole(snap.data().role || "");
          setUserName(snap.data().name || "");
        }
      } catch (err) {
        console.error("[Layout] Firestore error:", err);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const menuItemStyle = (path) => ({
    borderLeft: isActive(path, location) ? "3px solid #FFC000" : "3px solid transparent",
    backgroundColor: isActive(path, location) ? "rgba(255,192,0,0.12)" : "transparent",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
    transition: "all 0.2s ease",
  });

  const iconColor = (path) => ({
    color: isActive(path, location) ? "#FFC000" : "rgba(255,255,255,0.75)",
    minWidth: 0,
    mr: open ? 2.5 : "auto",
    justifyContent: "center",
  });

  const textColor = (path) => ({
    opacity: open ? 1 : 0,
    "& .MuiTypography-root": {
      color: isActive(path, location) ? "#FFC000" : "rgba(255,255,255,0.9)",
      fontFamily: "Abel",
      fontSize: 15,
      fontWeight: isActive(path, location) ? 600 : 400,
    },
  });

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={!isMobile && open}>
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ marginRight: 3, ...(!isMobile && open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>

          <div className="font-aref font-bold text-lg md:text-xl text-white tracking-wider">
            SALVATION ADMIN
          </div>

          <Box sx={{ position: "absolute", right: 16, display: "flex", alignItems: "center", gap: 1.5 }}>
            <div className="hidden md:block text-right">
              <div className="text-white text-sm font-abel leading-tight">
                {userName || userEmail?.split("@")[0]}
              </div>
              <div className="text-blue-200 text-xs font-abel capitalize">{userRole}</div>
            </div>
            <Avatar
              sx={{
                bgcolor: "#FFC000",
                color: "#000C66",
                width: 36,
                height: 36,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "Abel",
              }}
            >
              {(userName || userEmail || "A").charAt(0).toUpperCase()}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {(() => {
        const drawerContent = (
          <>
            <DrawerHeader>
              {open && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#FFC000] flex items-center justify-center">
                    <span className="text-[#000C66] font-bold text-sm">✝</span>
                  </div>
                  <span className="text-white font-aref font-bold text-base tracking-wider">SALVATION</span>
                </div>
              )}
              <IconButton onClick={handleDrawerClose} sx={{ color: "rgba(255,255,255,0.7)", ml: open ? 0 : "auto" }}>
                {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </DrawerHeader>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

            <List sx={{ pt: 1, flexGrow: 1 }}>
              {(userRole === "editor"
                ? menuItems.filter((m) => ["/home", "/event", "/article", "/author", "/members", "/birthdays"].includes(m.path))
                : userRole === "operator"
                ? menuItems.filter((m) => ["/home", "/event", "/members", "/birthdays", "/sbc-tracts", "/settings"].includes(m.path))
                : menuItems
              ).map((menu, index) => (
                <Tooltip key={index} title={!open ? menu.title : ""} placement="right" arrow>
                  <ListItem disablePadding sx={menuItemStyle(menu.path)} onClick={() => handleNavigate(menu.path)}>
                    <ListItemButton sx={{ minHeight: 46, justifyContent: open ? "initial" : "center", px: 2 }}>
                      <ListItemIcon sx={iconColor(menu.path)}>{menu.icon}</ListItemIcon>
                      <ListItemText primary={menu.title} sx={textColor(menu.path)} />
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              ))}

              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />

              {(userRole === "admin" || userRole === "superadmin") && (
                <Tooltip title={!open ? "Manage Admin" : ""} placement="right" arrow>
                  <ListItem disablePadding sx={menuItemStyle("/manage-admin")} onClick={() => handleNavigate("/manage-admin")}>
                    <ListItemButton sx={{ minHeight: 46, justifyContent: open ? "initial" : "center", px: 2 }}>
                      <ListItemIcon sx={iconColor("/manage-admin")}><AdminPanelSettings /></ListItemIcon>
                      <ListItemText primary="Manage Admin" sx={textColor("/manage-admin")} />
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              )}

              {userRole === "superadmin" && (
                <Tooltip title={!open ? "Notifications" : ""} placement="right" arrow>
                  <ListItem disablePadding sx={menuItemStyle("/notification")} onClick={() => handleNavigate("/notification")}>
                    <ListItemButton sx={{ minHeight: 46, justifyContent: open ? "initial" : "center", px: 2 }}>
                      <ListItemIcon sx={iconColor("/notification")}><NotificationImportant /></ListItemIcon>
                      <ListItemText primary="Notifications" sx={textColor("/notification")} />
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              )}

              <Tooltip title={!open ? "Logout" : ""} placement="right" arrow>
                <ListItem disablePadding sx={{ "&:hover": { backgroundColor: "rgba(255,80,80,0.15)" }, transition: "all 0.2s ease" }} onClick={() => setShow(true)}>
                  <ListItemButton sx={{ minHeight: 46, justifyContent: open ? "initial" : "center", px: 2 }}>
                    <ListItemIcon sx={{ color: "rgba(255,120,120,0.9)", minWidth: 0, mr: open ? 2.5 : "auto", justifyContent: "center" }}>
                      <ExitToApp />
                    </ListItemIcon>
                    <ListItemText primary="Logout" sx={{ opacity: open ? 1 : 0, "& .MuiTypography-root": { color: "rgba(255,120,120,0.9)", fontFamily: "Abel", fontSize: 15 } }} />
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            </List>

            {open && (
              <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="text-xs text-blue-300 font-abel truncate">{userEmail}</div>
              </Box>
            )}
          </>
        );

        return isMobile ? (
          <MuiDrawer
            variant="temporary"
            open={open}
            onClose={handleDrawerClose}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: mobilePaperSx }}
          >
            {drawerContent}
          </MuiDrawer>
        ) : (
          <PermanentDrawer variant="permanent" open={open} className="h-screen">
            {drawerContent}
          </PermanentDrawer>
        );
      })()}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: "#f5f7fe",
          minHeight: "100vh",
          width: isMobile ? "100%" : undefined,
          overflow: "hidden",
        }}
      >
        <DrawerHeader />
        <ToastContainer position="top-right" autoClose={3000} />
        {props.children}
      </Box>

      {inactiveWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⏱</span>
            </div>
            <h3 className="text-xl font-bold text-[#000C66] font-aref">Still there?</h3>
            <p className="text-gray-400 text-sm font-abel mt-2 mb-1">
              You've been inactive for 15 minutes.
            </p>
            <p className="text-gray-500 text-sm font-abel mb-6">
              You'll be signed out in{" "}
              <span className="font-bold text-[#000C66] text-base">{countdown}s</span>
            </p>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-6">
              <div
                className="bg-[#FFC000] h-1.5 rounded-full transition-all duration-1000"
                style={{ width: `${(countdown / WARNING_SECS) * 100}%` }}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={logout}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-500 font-abel font-medium hover:bg-gray-50 transition text-sm"
              >
                Sign Out
              </button>
              <button
                onClick={continueSession}
                className="flex-1 py-3 rounded-xl bg-[#000C66] text-white font-abel font-semibold hover:bg-blue-900 transition text-sm"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <ExitToApp className="text-red-500 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 font-aref">Sign Out</h3>
              <p className="text-gray-400 text-sm font-abel mt-1">Are you sure you want to sign out?</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShow(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-abel font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={logout}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-abel font-semibold hover:bg-red-600 transition"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}
